import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardCard } from '../components/DashboardComponents';
import { Users, UserPlus, Truck, Loader, Calendar, Search, ChevronDown, ChevronUp, Menu, AlertCircle, UserCheck, Clock, FileCheck, ChevronLeft, ChevronRight, Package, MapPin, Edit, UserX } from 'lucide-react';
import SideNavigation from '../components/SideNavigation';
import api from '../utils/api';
import useScreenSize from '../hooks/useScreenSize';
import Modal from '../components/Modal';
import { fetchNotifications } from '../store/slices/notificationSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const DriverManagement = () => {
  const dispatch = useDispatch();
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDriverModalOpen, setIsAddDriverModalOpen] = useState(false);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [dashboardData, setDashboardData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1,
    perPage: 10
  });

  const fetchDrivers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/drivers?page=${currentPage}&per_page=${pagination.perPage}`);
      setDrivers(response.data.drivers);
      setPagination({
        total: response.data.total,
        pages: response.data.pages,
        perPage: response.data.per_page
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pagination.perPage]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get('/api/carrier/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  useEffect(() => {
    fetchDrivers();
    fetchDashboardData();
  }, [fetchDrivers, fetchDashboardData]);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement driver search logic here
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const filteredDrivers = drivers.filter(driver => 
    driver.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage error={error} />;

  return (
    <div className="flex h-screen bg-gray-100">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />
      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="max-w-7xl mx-auto py-4 sm:py-6 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4 px-2">
            {(isMobile || isTablet) && (
              <button
                onClick={toggleSidebar}
                className="p-2 bg-white rounded-md shadow-md"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 ml-2">
              Driver Management
            </h1>
          </div>

          {/* Search and Add Driver - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6 px-2">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </form>
            <button
              onClick={() => setIsAddDriverModalOpen(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center justify-center"
            >
              <UserPlus className="h-5 w-5 mr-2" />
              Add Driver
            </button>
          </div>

          {/* Dashboard Cards - Mobile Grid */}
          {dashboardData && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 px-2">
              <DashboardCard 
                icon={Users} 
                title="Total Drivers" 
                value={drivers.length} 
              />
              <DashboardCard 
                icon={UserCheck} 
                title="Active Drivers" 
                value={drivers.filter(d => d.status === 'active').length} 
              />
              <DashboardCard 
                icon={Truck} 
                title="Drivers on Route" 
                value={drivers.filter(d => d.status === 'on_route').length} 
              />
              <DashboardCard 
                icon={FileCheck} 
                title="Expiring Licenses" 
                value={drivers.filter(d => isLicenseExpiringSoon(d.licenseExpiry)).length} 
                alert={true}
              />
            </div>
          )}

          {/* Driver List */}
          <div className="space-y-4 px-2">
            <AnimatePresence>
              {filteredDrivers.length > 0 ? (
                filteredDrivers.map((driver) => (
                  <DriverCard key={driver.id} driver={driver} onUpdate={fetchDrivers} />
                ))
              ) : (
                <EmptyState />
              )}
            </AnimatePresence>
          </div>

          {/* Mobile-Optimized Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 px-2">
            <div className="w-full sm:w-auto mb-4 sm:mb-0">
              <p className="text-sm text-gray-700 text-center sm:text-left">
                Showing <span className="font-medium">{(currentPage - 1) * pagination.perPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pagination.perPage, pagination.total)}</span> of{' '}
                <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <nav className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md bg-white shadow-sm disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <span className="px-4 py-2 text-sm">
                Page {currentPage} of {pagination.pages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.pages}
                className="p-2 rounded-md bg-white shadow-sm disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </main>

      <AddDriverModal 
        isOpen={isAddDriverModalOpen} 
        onClose={() => setIsAddDriverModalOpen(false)}
        onDriverAdded={fetchDrivers}
      />

      <ToastContainer
        position={isMobile ? "bottom-center" : "bottom-right"}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={3}
      />
    </div>
  );
};

const DriverCard = ({ driver, onUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [assignedLoads, setAssignedLoads] = useState([]);
  const [isLoadingLoads, setIsLoadingLoads] = useState(false);

  // Update how we get current loads
  const currentLoads = driver.currentLoads || [];

  useEffect(() => {
    const fetchAssignedLoads = async () => {
      if (isExpanded) {
        try {
          setIsLoadingLoads(true);
          const response = await api.get(`/api/drivers/${driver.id}/available-loads`);
          setAssignedLoads(response.data.available_loads);
        } catch (error) {
          console.error('Error fetching assigned loads:', error);
        } finally {
          setIsLoadingLoads(false);
        }
      }
    };

    fetchAssignedLoads();
  }, [driver.id, isExpanded]);

  const handleStatusChange = async () => {
    try {
      setIsUpdating(true);
      const newStatus = driver.status === 'active' ? 'inactive' : 'active';
      await api.patch(`/api/drivers/${driver.id}/status`, { status: newStatus });
      onUpdate();
    } catch (error) {
      console.error('Error updating driver status:', error);
      // Optionally add error handling UI feedback here
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <motion.div 
      className="bg-white shadow-lg rounded-lg p-4 sm:p-6 transition-all duration-300 relative hover:shadow-xl cursor-pointer"
      whileHover={{ y: -5 }}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <StatusBadge 
        status={driver.status} 
        load={driver}
      />
      
      <div className="absolute top-2 right-2 z-10 pointer-events-none"> 
        {isExpanded ? 
          <ChevronUp className="text-gray-500" /> : 
          <ChevronDown className="text-gray-500" />
        }
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            {driver.profilePicture ? (
              <img 
                src={driver.profilePicture} 
                alt={`${driver.firstName} ${driver.lastName}`} 
                className="h-12 w-12 rounded-full" 
              />
            ) : (
              <Users className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div className="ml-4">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
              {driver.firstName} {driver.lastName}
            </h3>
            <p className="text-sm text-gray-500">License: {driver.licenseNumber}</p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <InfoItem label="Phone" value={driver.phone || 'N/A'} />
              <InfoItem label="Email" value={driver.email || 'N/A'} />
              <InfoItem label="Experience" value={`${driver.yearsOfExperience || 0} years`} />
              <InfoItem 
                label="License Expiry" 
                value={driver.licenseExpiry ? new Date(driver.licenseExpiry).toLocaleDateString() : 'N/A'}
                alert={isLicenseExpiringSoon(driver.licenseExpiry)}
              />
              <InfoItem 
                label="Preferred Routes" 
                value={Array.isArray(driver.preferredRoutes) ? driver.preferredRoutes.join(', ') : 'None'} 
              />
              <LoadsInfoItem 
                label="Current Loads" 
                loads={currentLoads}
              />
            </div>

            {/* Assigned Loads Section */}
            <div className="mt-6 border-t pt-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Truck className="h-5 w-5 mr-2" />
                  Assigned Loads
                </div>
                <span className="text-sm font-normal text-gray-500">
                  {assignedLoads.length} {assignedLoads.length === 1 ? 'load' : 'loads'}
                </span>
              </h4>
              
              {isLoadingLoads ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : assignedLoads.length > 0 ? (
                <div className="relative">
                  {/* Scrollable Container */}
                  <div 
                    className="space-y-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                    style={{
                      scrollbarWidth: 'thin',
                      msOverflowStyle: 'none'
                    }}
                  >
                    {assignedLoads.map((load) => (
                      <LoadCard key={load.id} load={load} />
                    ))}
                  </div>
                  
                  {/* Scroll Indicators */}
                  <div className="absolute top-0 right-0 bottom-0 w-2 pointer-events-none">
                    <div className="h-full bg-gradient-to-b from-gray-50 via-transparent to-gray-50 opacity-75" />
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <Package className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No loads currently assigned</p>
                </div>
              )}
            </div>

            <div className="flex space-x-2 mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditMode(true);
                }}
                className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Details
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange();
                }}
                disabled={isUpdating}
                className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                  driver.status === 'inactive'
                    ? 'text-green-600 hover:bg-green-50'
                    : 'text-red-600 hover:bg-red-50'
                } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isUpdating ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : driver.status === 'inactive' ? (
                  <UserCheck className="h-4 w-4 mr-2" />
                ) : (
                  <UserX className="h-4 w-4 mr-2" />
                )}
                {isUpdating ? 'Updating...' : (driver.status === 'inactive' ? 'Activate' : 'Deactivate')}
              </button>
            </div>

            <EditDriverModal
              isOpen={isEditMode}
              onClose={() => setIsEditMode(false)}
              driver={driver}
              onUpdate={onUpdate}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// New component for displaying loads
const LoadsInfoItem = ({ label, loads }) => (
  <div>
    <p className="text-sm font-medium text-gray-600">{label}</p>
    {loads.length > 0 ? (
      <div className="flex flex-wrap gap-1.5 mt-1">
        {loads.map((load) => (
          <span
            key={load.id}
            className="inline-flex items-center px-2 py-0.5 rounded text-sm bg-blue-50 text-blue-700 border border-blue-100"
          >
            #{load.id}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-base text-gray-800">None</p>
    )}
  </div>
);

const AddDriverModal = ({ isOpen, onClose, onDriverAdded }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseExpiry: '',
    yearsOfExperience: '',
    preferredRoutes: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure phone is in E.164 format for new records
      // The phone input component will handle this internally
      await api.post('/api/drivers', formData);
      onDriverAdded();
      onClose();
      toast.success('Driver added successfully', {
        position: window.innerWidth <= 768 ? "bottom-center" : "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } catch (error) {
      console.error('Error adding driver:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add driver';
      toast.error(errorMessage, {
        position: window.innerWidth <= 768 ? "bottom-center" : "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Driver">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
            required={true}
          />
          <InputField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData({...formData, lastName: e.target.value})}
            required={true}
          />
        </div>
        <InputField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          required={false}
        />
        
        {/* Replace phone input with international phone component */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            country={'us'}
            value={formData.phone}
            onChange={(value) => setFormData({...formData, phone: '+' + value})}
            inputClass="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            containerClass="w-full"
            enableSearch={true}
            disableSearchIcon={true}
            searchPlaceholder="Search country..."
            required
          />
          <p className="mt-1 text-xs text-gray-500">Include country code for international numbers</p>
        </div>
        
        <InputField
          label="License Number"
          value={formData.licenseNumber}
          onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
          required={false}
        />
        <InputField
          label="License Expiry"
          type="date"
          value={formData.licenseExpiry}
          onChange={(e) => setFormData({...formData, licenseExpiry: e.target.value})}
          required={false}
        />
        <InputField
          label="Years of Experience"
          type="number"
          value={formData.yearsOfExperience}
          onChange={(e) => setFormData({...formData, yearsOfExperience: e.target.value})}
          required={false}
        />
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Add Driver
          </button>
        </div>
      </form>
    </Modal>
  );
};

const EditDriverModal = ({ isOpen, onClose, driver, onUpdate }) => {
  const [formData, setFormData] = useState({
    firstName: driver.firstName,
    lastName: driver.lastName,
    phone: driver.phone,
    email: driver.email,
    licenseNumber: driver.licenseNumber,
    licenseExpiry: driver.licenseExpiry ? new Date(driver.licenseExpiry).toISOString().split('T')[0] : '',
    yearsOfExperience: driver.yearsOfExperience,
    preferredRoutes: driver.preferredRoutes || []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Get the default country code from the phone number using dial code mapping
  const getDefaultCountry = () => {
    const phone = formData.phone;
    if (!phone || !phone.startsWith('+')) return 'us';
    
    // Map of common dial codes to ISO country codes
    // Ordered by specificity (longer codes first to handle countries like +1 vs +1246)
    const dialCodeToCountry = {
      '+1246': 'bb', // Barbados
      '+1264': 'ai', // Anguilla
      '+1268': 'ag', // Antigua and Barbuda
      '+1284': 'vg', // British Virgin Islands
      '+1340': 'vi', // US Virgin Islands
      '+1345': 'ky', // Cayman Islands
      '+1441': 'bm', // Bermuda
      '+1473': 'gd', // Grenada
      '+1649': 'tc', // Turks and Caicos
      '+1664': 'ms', // Montserrat
      '+1670': 'mp', // Northern Mariana Islands
      '+1671': 'gu', // Guam
      '+1684': 'as', // American Samoa
      '+1758': 'lc', // Saint Lucia
      '+1767': 'dm', // Dominica
      '+1784': 'vc', // Saint Vincent and the Grenadines
      '+1787': 'pr', // Puerto Rico
      '+1809': 'do', // Dominican Republic
      '+1868': 'tt', // Trinidad and Tobago
      '+1869': 'kn', // Saint Kitts and Nevis
      '+1876': 'jm', // Jamaica
      '+1939': 'pr', // Puerto Rico (alternate)
      '+1': 'us',    // US/Canada (default for +1)
      '+44': 'gb',   // United Kingdom
      '+49': 'de',   // Germany
      '+33': 'fr',   // France
      '+34': 'es',   // Spain
      '+39': 'it',   // Italy
      '+31': 'nl',   // Netherlands
      '+32': 'be',   // Belgium
      '+41': 'ch',   // Switzerland
      '+43': 'at',   // Austria
      '+45': 'dk',   // Denmark
      '+46': 'se',   // Sweden
      '+47': 'no',   // Norway
      '+48': 'pl',   // Poland
      '+52': 'mx',   // Mexico
      '+55': 'br',   // Brazil
      '+54': 'ar',   // Argentina
      '+56': 'cl',   // Chile
      '+57': 'co',   // Colombia
      '+58': 've',   // Venezuela
      '+61': 'au',   // Australia
      '+64': 'nz',   // New Zealand
      '+81': 'jp',   // Japan
      '+82': 'kr',   // South Korea
      '+86': 'cn',   // China
      '+91': 'in',   // India
      '+92': 'pk',   // Pakistan
      '+93': 'af',   // Afghanistan
      '+94': 'lk',   // Sri Lanka
      '+95': 'mm',   // Myanmar
      '+60': 'my',   // Malaysia
      '+62': 'id',   // Indonesia
      '+63': 'ph',   // Philippines
      '+65': 'sg',   // Singapore
      '+66': 'th',   // Thailand
      '+84': 'vn',   // Vietnam
      '+971': 'ae',  // UAE
      '+972': 'il',  // Israel
      '+966': 'sa',  // Saudi Arabia
      '+20': 'eg',   // Egypt
      '+27': 'za',   // South Africa
      '+234': 'ng',  // Nigeria
      '+254': 'ke',  // Kenya
      '+7': 'ru',    // Russia/Kazakhstan
    };
    
    // Check longer dial codes first (more specific)
    const sortedCodes = Object.keys(dialCodeToCountry).sort((a, b) => b.length - a.length);
    for (const code of sortedCodes) {
      if (phone.startsWith(code)) {
        return dialCodeToCountry[code];
      }
    }
    
    return 'us'; // Default fallback
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await api.patch(`/api/drivers/${driver.id}`, formData);
      onUpdate();
      onClose();
      toast.success('Driver updated successfully', {
        position: window.innerWidth <= 768 ? "bottom-center" : "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update driver');
      toast.error('Failed to update driver', {
        position: window.innerWidth <= 768 ? "bottom-center" : "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit Driver: ${driver.firstName} ${driver.lastName}`}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
            required={true}
          />
          <InputField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData({...formData, lastName: e.target.value})}
            required={true}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required={false}
          />
          
          {/* Replace the phone input field with international version */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <PhoneInput
              country={getDefaultCountry()}
              value={formData.phone}
              onChange={(value) => {
                // Preserve the + for E.164 format
                const formattedNumber = value.startsWith('+') ? value : '+' + value;
                setFormData({...formData, phone: formattedNumber});
              }}
              inputClass="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              containerClass="w-full"
              enableSearch={true}
              disableSearchIcon={true}
              searchPlaceholder="Search country..."
              required
            />
            <p className="mt-1 text-xs text-gray-500">Include country code for international numbers</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="License Number"
            value={formData.licenseNumber}
            onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
            required={false}
          />
          <InputField
            label="License Expiry"
            type="date"
            value={formData.licenseExpiry}
            onChange={(e) => setFormData({...formData, licenseExpiry: e.target.value})}
            required={false}
          />
        </div>

        <InputField
          label="Years of Experience"
          type="number"
          min="0"
          value={formData.yearsOfExperience}
          onChange={(e) => setFormData({...formData, yearsOfExperience: e.target.value})}
          required={false}
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Preferred Routes
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['Local', 'Regional', 'Long Haul', 'Interstate'].map((route) => (
              <label
                key={route}
                className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={formData.preferredRoutes.includes(route)}
                  onChange={() => {
                    const updatedRoutes = formData.preferredRoutes.includes(route)
                      ? formData.preferredRoutes.filter(r => r !== route)
                      : [...formData.preferredRoutes, route];
                    setFormData({...formData, preferredRoutes: updatedRoutes});
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{route}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Updating...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Helper components
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-full">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
  </div>
);

const ErrorMessage = ({ error }) => (
  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
    <p className="font-bold">Error</p>
    <p>{error}</p>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-12">
    <Users className="mx-auto h-12 w-12 text-gray-400" />
    <h3 className="mt-2 text-sm font-medium text-gray-900">No drivers found</h3>
    <p className="mt-1 text-sm text-gray-500">Get started by adding a new driver.</p>
  </div>
);

const StatusBadge = ({ status, load }) => {
  // Determine the actual status based on all current loads
  const getDisplayStatus = () => {
    // If no loads array, return base status
    if (!load?.currentLoads) return 'active';
    
    // Check if ANY of the current loads have an active status
    const hasActiveLoad = load.currentLoads.some(load => {
      const status = load.status;
      
      // Check for trip_started
      if (status === 'trip_started') return true;
      
      // Check for any at_stop_X pattern
      if (/^at_stop_\d+$/.test(status)) return true;
      
      // Check for any in_transit_X_Y pattern
      if (/^in_transit_\d+_\d+$/.test(status)) return true;
      
      return false;
    });

    // If any load is active, show on_route, otherwise show active
    return hasActiveLoad ? 'on_route' : 'active';
  };

  const displayStatus = getDisplayStatus();

  const statusColors = {
    active: 'bg-green-500',
    inactive: 'bg-gray-500',
    on_route: 'bg-blue-500',
    suspended: 'bg-red-500'
  };

  const statusText = {
    active: 'ACTIVE',
    inactive: 'INACTIVE',
    on_route: 'ON ROUTE',
    suspended: 'SUSPENDED'
  };

  return (
    <span className={`absolute top-0 right-0 transform translate-x-2 -translate-y-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white ${statusColors[displayStatus]} shadow-md`}>
      {statusText[displayStatus]}
    </span>
  );
};

const InfoItem = ({ label, value, alert }) => (
  <div>
    <p className="text-sm font-medium text-gray-600">{label}</p>
    <p className={`text-base ${alert ? 'text-red-600 font-semibold' : 'text-gray-800'}`}>
      {label === "Phone" ? formatPhoneForDisplay(value) : 
       (typeof value === 'object' ? JSON.stringify(value) : value)}
    </p>
  </div>
);

const InputField = ({ label, type = "text", value, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

const isLicenseExpiringSoon = (expiryDate) => {
  const expiry = new Date(expiryDate);
  const today = new Date();
  const daysUntilExpiry = (expiry - today) / (1000 * 60 * 60 * 24);
  return daysUntilExpiry <= 30;
};

const getLoadStatusColor = (status) => {
  const statusColors = {
    open: 'bg-gray-100 text-gray-800',
    assigned: 'bg-yellow-100 text-yellow-800',
    assigned_driver: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
  };

  if (status.startsWith('at_stop_')) {
    return 'bg-purple-100 text-purple-800';
  }
  if (status.startsWith('in_transit_')) {
    return 'bg-indigo-100 text-indigo-800';
  }

  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

const LoadCard = ({ load }) => (
  <div 
    key={load.id}
    className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
  >
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
      <div className="flex items-center">
        <Package className="h-5 w-5 text-blue-500 mr-2" />
        <span className="font-medium">Load #{load.id}</span>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
        getLoadStatusColor(load.status)
      }`}>
        {formatLoadStatus(load.status)}
      </span>
    </div>
    
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      {/* Origin Information */}
      <div className="space-y-2">
        <h4 className="font-medium text-gray-700">Pickup Location</h4>
        <div className="flex items-start space-x-2">
          <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
          <div>
            <p>{load.origin_address}</p>
            <p>{`${load.origin_city}, ${load.origin_state} ${load.origin_zip}`}</p>
            <p className="text-gray-500">
              {formatDateTime(load.pickup_date, load.pickup_time)}
            </p>
          </div>
        </div>
      </div>

      {/* Destination Information */}
      <div className="space-y-2">
        <h4 className="font-medium text-gray-700">Delivery Location</h4>
        <div className="flex items-start space-x-2">
          <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
          <div>
            <p>{load.destination_address}</p>
            <p>{`${load.destination_city}, ${load.destination_state} ${load.destination_zip}`}</p>
            <p className="text-gray-500">
              {formatDateTime(load.delivery_date, load.delivery_time)}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Load Details */}
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
      <DetailItem label="Weight" value={`${load.weight} lbs`} />
      <DetailItem label="Pieces" value={load.pieces} />
      <DetailItem label="Type" value={load.load_type} />
      <DetailItem label="Customer" value={load.customer_name} />
    </div>

    {/* Special Requirements */}
    {load.special_requirements && (
      <div className="mt-4 p-3 bg-yellow-50 rounded-md">
        <p className="text-sm text-yellow-800">
          <AlertCircle className="inline-block h-4 w-4 mr-2" />
          {load.special_requirements}
        </p>
      </div>
    )}
  </div>
);

const DetailItem = ({ label, value }) => (
  <div>
    <p className="text-gray-500">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);

const formatDateTime = (date, time) => {
  const dateObj = new Date(date);
  const formattedDate = dateObj.toLocaleDateString();
  const formattedTime = time ? time.slice(0, 5) : ''; // Format HH:mm from HH:mm:ss
  return `${formattedDate} ${formattedTime}`;
};

const formatLoadStatus = (status) => {
  if (status.startsWith('at_stop_')) {
    return 'At Stop';
  } else if (status.startsWith('in_transit_')) {
    return 'In Transit';
  }
  return status.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Helper function to format phone numbers for display (supports international formats)
const formatPhoneForDisplay = (phone) => {
  if (!phone) return 'N/A';
  
  // Handle E.164 format (starting with '+')
  if (phone.startsWith('+')) {
    try {
      // US/Canada format: +1XXXXXXXXXX -> +1 (XXX) XXX-XXXX
      if (phone.startsWith('+1') && phone.length === 12) {
        return `+1 (${phone.substring(2, 5)}) ${phone.substring(5, 8)}-${phone.substring(8)}`;
      }
      
      // Mexico format: +52XXXXXXXXXX -> +52 XX XXXX XXXX
      if (phone.startsWith('+52')) {
        const rest = phone.substring(3);
        if (rest.length === 10) {
          // Standard 10-digit Mexican format: XX XXXX XXXX
          return `+52 ${rest.substring(0, 2)} ${rest.substring(2, 6)} ${rest.substring(6)}`;
        }
        // For non-standard lengths, use balanced 3-digit grouping
        if (rest.length >= 6) {
          const groups = [];
          let i = 0;
          while (i < rest.length) {
            const remaining = rest.length - i;
            if (remaining <= 4) {
              groups.push(rest.substring(i));
              break;
            }
            groups.push(rest.substring(i, i + 3));
            i += 3;
          }
          return `+52 ${groups.join(' ')}`;
        }
        return `+52 ${rest}`;
      }
      
      // UK format: +44XXXXXXXXXX -> +44 XXXX XXXXXX
      if (phone.startsWith('+44')) {
        const rest = phone.substring(3);
        if (rest.length >= 9) {
          return `+44 ${rest.substring(0, 4)} ${rest.substring(4)}`;
        }
        return `+44 ${rest}`;
      }
      
      // India format: +91XXXXXXXXXX -> +91 XXXXX XXXXX
      if (phone.startsWith('+91')) {
        const rest = phone.substring(3);
        if (rest.length === 10) {
          return `+91 ${rest.substring(0, 5)} ${rest.substring(5)}`;
        }
        return `+91 ${rest}`;
      }
      
      // Germany format: +49XXXXXXXXXX -> +49 XXX XXXXXXX
      if (phone.startsWith('+49')) {
        const rest = phone.substring(3);
        if (rest.length >= 9) {
          return `+49 ${rest.substring(0, 3)} ${rest.substring(3)}`;
        }
        return `+49 ${rest}`;
      }
      
      // France format: +33XXXXXXXXX -> +33 X XX XX XX XX
      if (phone.startsWith('+33')) {
        const rest = phone.substring(3);
        if (rest.length === 9) {
          return `+33 ${rest[0]} ${rest.substring(1, 3)} ${rest.substring(3, 5)} ${rest.substring(5, 7)} ${rest.substring(7)}`;
        }
        return `+33 ${rest}`;
      }
      
      // Generic international format with proper country code detection
      // Known 2-digit country codes (most common)
      const twoDigitCodes = ['20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98'];
      
      let countryCode;
      let nationalNumber;
      
      // Check for single-digit country codes (+1, +7)
      if (phone[1] === '1' || phone[1] === '7') {
        countryCode = phone.substring(0, 2);
        nationalNumber = phone.substring(2);
      }
      // Check for known 2-digit country codes
      else if (twoDigitCodes.includes(phone.substring(1, 3))) {
        countryCode = phone.substring(0, 3);
        nationalNumber = phone.substring(3);
      }
      // Default to 3-digit country code
      else {
        const match = phone.match(/^(\+\d{1,3})(.*)$/);
        if (match) {
          countryCode = match[1];
          nationalNumber = match[2];
        }
      }
      
      if (countryCode && nationalNumber) {
        // Smart grouping to avoid trailing short segments
        const groups = [];
        let i = 0;
        while (i < nationalNumber.length) {
          const remaining = nationalNumber.length - i;
          if (remaining <= 4) {
            groups.push(nationalNumber.substring(i));
            break;
          }
          groups.push(nationalNumber.substring(i, i + 3));
          i += 3;
        }
        return `${countryCode} ${groups.join(' ')}`.trim();
      }
      
      return phone;
    } catch (e) {
      return phone; // Fall back to raw format
    }
  }
  
  // Legacy format (no +), assume US
  if (phone.length === 10) {
    return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`;
  }
  
  // Return as-is for other formats
  return phone;
};

export default DriverManagement;
